import { FIXED_LIMITS, Permissions, serializeBitfield, type PermissionOverwriteDto } from '@lagerfeuer/shared';
import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { Modal } from '../../components/Modal';
import { errorMessage } from '../../lib/api';
import { channelsApi, type ChannelCreate } from '../../lib/endpoints';
import { paths } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { actorFor, roleColor } from '../server-settings/hierarchy';
import { ChannelIcon } from '../shell/channelIcons';
import { normalizeChannelName } from './channelHelpers';
import '../server-settings/server-settings.css';
import './channel-settings.css';

type CreatableType = ChannelCreate['type'];

const TYPES: { type: CreatableType; label: string; hint: string }[] = [
  { type: 'TEXT', label: 'Text', hint: 'Nachrichten, Bilder, GIFs, Emojis und Meinungen' },
  { type: 'VOICE', label: 'Sprache', hint: 'Gemeinsam reden, Kamera und Bildschirm teilen' },
  { type: 'FORUM', label: 'Forum', hint: 'Beiträge mit Titel und Tags für geordnete Diskussionen' },
  { type: 'ANNOUNCEMENT', label: 'Ankündigungen', hint: 'Wichtige Neuigkeiten, in denen meist nur wenige schreiben' },
  { type: 'CATEGORY', label: 'Kategorie', hint: 'Kanäle gruppieren und Rechte gemeinsam verwalten' },
];

/** Kanal oder Kategorie anlegen: Typ, Name, Kategorie, optional privat (nur ausgewählte Rollen). */
export function CreateChannelDialog({
  serverId,
  parentId,
  defaultType = 'TEXT',
  onClose,
}: {
  serverId: string;
  parentId?: string | null;
  defaultType?: 'TEXT' | 'VOICE' | 'CATEGORY' | 'FORUM' | 'ANNOUNCEMENT';
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const channels = useAppStore((s) => s.channels);
  const roles = useAppStore((s) => s.roles[serverId]);
  const categories = useMemo(() => Object.values(channels).filter((c) => c.serverId === serverId && c.type === 'CATEGORY').sort((a, b) => a.position - b.position), [channels, serverId]);
  const [type, setType] = useState<CreatableType>(defaultType);
  const [name, setName] = useState('');
  const [parent, setParent] = useState<string>(parentId ?? '');
  const [priv, setPriv] = useState(false);
  const [allowed, setAllowed] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectableRoles = (roles ?? []).filter((r) => !r.isEveryone && !r.managed).sort((a, b) => b.position - a.position);
  const isCategory = type === 'CATEGORY';
  const finalName = normalizeChannelName(name.trim(), type);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!finalName) return;
    setBusy(true);
    setError(null);
    const body: ChannelCreate = { type, name: finalName };
    if (!isCategory && parent) body.parentId = parent;
    if (priv) {
      const access = Permissions.VIEW_CHANNEL | (type === 'VOICE' ? Permissions.CONNECT : 0n);
      const ow: PermissionOverwriteDto[] = [{ targetType: 'ROLE', targetId: serverId, allow: '0', deny: serializeBitfield(Permissions.VIEW_CHANNEL) }];
      for (const id of allowed) ow.push({ targetType: 'ROLE', targetId: id, allow: serializeBitfield(access), deny: '0' });
      const actor = actorFor(useAppStore.getState(), serverId);
      if (actor && !actor.isOwner && !actor.isAdmin) ow.push({ targetType: 'MEMBER', targetId: actor.meId, allow: serializeBitfield(access), deny: '0' });
      body.permissionOverwrites = ow;
    }
    try {
      const ch = await channelsApi.create(serverId, body);
      useAppStore.getState().dispatch('CHANNEL_CREATE', ch);
      onClose();
      if (ch.type === 'TEXT' || ch.type === 'ANNOUNCEMENT' || ch.type === 'FORUM') void navigate(paths.channel(serverId, ch.id));
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isCategory ? 'Kategorie erstellen' : 'Kanal erstellen'}
      onClose={onClose}
      testId="create-channel-dialog"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" form="create-channel-form" className="btn btn-primary" disabled={busy || !finalName} data-testid="create-channel-submit">
            {busy ? 'Wird erstellt …' : isCategory ? 'Kategorie erstellen' : 'Kanal erstellen'}
          </button>
        </>
      }
    >
      <form id="create-channel-form" className="stack" onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor="create-channel-name">{isCategory ? 'Name der Kategorie' : 'Kanalname'}</label>
          <div className="channel-name-input">
            <ChannelIcon type={type} size={18} locked={priv} />
            <input
              id="create-channel-name"
              className="input"
              value={name}
              placeholder={isCategory ? 'Neue Kategorie' : type === 'VOICE' ? 'Am Feuer' : 'neuer-kanal'}
              maxLength={FIXED_LIMITS.channelNameMax}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              data-testid="create-channel-name"
            />
          </div>
          {finalName && finalName !== name.trim() && <span className="field-hint">Wird gespeichert als „{finalName}".</span>}
        </div>
        <fieldset className="channel-type-list" data-testid="create-channel-types">
          <legend className="label">Typ</legend>
          {TYPES.map((t) => (
            <label key={t.type} className="channel-type-card">
              <input type="radio" name="channel-type" value={t.type} checked={type === t.type} onChange={() => setType(t.type)} data-testid={`create-channel-type-${t.type}`} />
              <ChannelIcon type={t.type} size={22} />
              <span className="channel-type-text">
                <strong>{t.label}</strong>
                <span className="muted small">{t.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {!isCategory && categories.length > 0 && (
          <div className="field">
            <label htmlFor="create-channel-parent">Kategorie</label>
            <select id="create-channel-parent" className="select" value={parent} onChange={(e) => setParent(e.target.value)} data-testid="create-channel-parent">
              <option value="">Keine Kategorie</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {parent && !priv && <span className="field-hint">Übernimmt die Berechtigungen der Kategorie (synchronisiert).</span>}
          </div>
        )}
        <label className="switch-row">
          <input type="checkbox" className="switch" checked={priv} onChange={(e) => setPriv(e.target.checked)} data-testid="create-channel-private" />
          <span>
            <strong>{isCategory ? 'Private Kategorie' : 'Privater Kanal'}</strong>
            <span className="field-hint" style={{ display: 'block' }}>
              Nur ausgewählte Rollen (und Admins) können {isCategory ? 'die Kategorie' : 'den Kanal'} sehen.
            </span>
          </span>
        </label>
        {priv && selectableRoles.length > 0 && (
          <fieldset className="radio-group" data-testid="create-channel-roles">
            <legend className="label">Wer hat Zugang?</legend>
            {selectableRoles.map((r) => (
              <label key={r.id} className="radio-card compact">
                <input
                  type="checkbox"
                  checked={allowed.has(r.id)}
                  onChange={(e) => {
                    const next = new Set(allowed);
                    if (e.target.checked) next.add(r.id);
                    else next.delete(r.id);
                    setAllowed(next);
                  }}
                  data-testid={`create-channel-role-${r.id}`}
                />
                <span className="role-dot" style={{ background: roleColor(r.color) ?? undefined }} aria-hidden="true" />
                {r.name}
              </label>
            ))}
          </fieldset>
        )}
        {error && (
          <p className="error-text" role="alert" data-testid="create-channel-error">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
